import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchUnacceptedFilings, notifyFilingCPAs } from "../../../api/customAdmin/customAdminService";
import SmallAlert from "../../../components/ui/alerts/SmallAlert";

const PAGE_SIZE = 10;

const UnacceptedFilingsSection = () => {
  const navigate = useNavigate();
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notifying, setNotifying] = useState(null);
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });
  const [page, setPage] = useState(1);

  const loadFilings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchUnacceptedFilings();

      if (result.status === 401 || result.status === 403) {
        navigate("/custom-admin", { replace: true });
        return;
      }

      const data = result.filings ?? result.results ?? result.data ?? result;
      const sorted = Array.isArray(data)
        ? [...data].sort((a, b) => new Date(b.date_started) - new Date(a.date_started))
        : [];
      setFilings(sorted);
    } catch (e) {
      console.error("Error loading unaccepted filings:", e);
      setError("Failed to load unaccepted filings");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadFilings();
  }, [loadFilings]);

  const handleNotify = async (filingUuid) => {
    setNotifying(filingUuid);
    try {
      await notifyFilingCPAs(filingUuid);
      setAlert({
        show: true,
        type: "success",
        message: "CPAs notified successfully!",
      });
    } catch (e) {
      console.error("Error notifying CPAs:", e);
      setAlert({
        show: true,
        type: "error",
        message: "Failed to notify CPAs",
      });
    } finally {
      setNotifying(null);
    }
  };

  if (loading) {
    return <div>Loading unaccepted filings...</div>;
  }

  if (error) {
    return <div style={{ color: "red" }}>{error}</div>;
  }

  return (
    <>
      {alert.show && (
        <SmallAlert
          alert="True"
          type={alert.type}
          message={alert.message}
          dismissOnClickOutside={true}
          onDismiss={() => setAlert({ show: false, type: "", message: "" })}
          style={{ marginBottom: "20px" }}
        />
      )}

      <div>
        <h3>Unaccepted Filings</h3>

        {filings.length === 0 ? (
          <p>No unaccepted filings found.</p>
        ) : (() => {
          const totalPages = Math.ceil(filings.length / PAGE_SIZE);
          const paginated = filings.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
          return (
            <>
              <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "20px" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #ddd" }}>
                    <th style={{ padding: "12px", textAlign: "left" }}>Filing UUID</th>
                    <th style={{ padding: "12px", textAlign: "left" }}>Taxpayer</th>
                    <th style={{ padding: "12px", textAlign: "left" }}>Tax Type</th>
                    <th style={{ padding: "12px", textAlign: "left" }}>Status</th>
                    <th style={{ padding: "12px", textAlign: "left" }}>Date Started</th>
                    <th style={{ padding: "12px", textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((filing) => (
                    <tr key={filing.filing_uuid} style={{ borderBottom: "1px solid #eee" }}>
                      <td style={{ padding: "12px", fontFamily: "monospace", fontSize: "13px", color: "#555" }}>
                        {filing.filing_uuid}
                      </td>
                      <td style={{ padding: "12px" }}>
                        {filing.client
                          ? `${filing.client.first_name} ${filing.client.last_name}`
                          : "—"}
                      </td>
                      <td style={{ padding: "12px", fontSize: "13px" }}>
                        {(() => {
                          const services = filing.tax_service?.services ?? [];
                          const labels = services.map((s) =>
                            s === "PTF" ? "Personal" : s === "BTF" ? "Business" : s
                          );
                          return labels.length ? labels.join(", ") : "—";
                        })()}
                      </td>
                      <td style={{ padding: "12px" }}>
                        <span
                          style={{
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            fontWeight: "500",
                            backgroundColor: "#E6F3FF",
                            color: "#0066CC",
                          }}
                        >
                          Active
                        </span>
                      </td>
                      <td style={{ padding: "12px", fontSize: "13px", color: "#555" }}>
                        {filing.date_started
                          ? new Date(filing.date_started).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })
                          : "—"}
                      </td>
                      <td style={{ padding: "12px", textAlign: "center" }}>
                        <button
                          className="cta-button primary short"
                          onClick={() => handleNotify(filing.filing_uuid)}
                          disabled={notifying === filing.filing_uuid}
                        >
                          {notifying === filing.filing_uuid ? "Notifying..." : "Notify"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "20px" }}>
                <span style={{ fontSize: "13px", color: "#666" }}>
                  Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filings.length)} of {filings.length}
                </span>
                <div style={{ display: "flex", gap: "6px" }}>
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    style={{
                      padding: "6px 14px",
                      border: "1px solid #d0d0d0",
                      borderRadius: "6px",
                      background: page === 1 ? "#f5f5f5" : "white",
                      color: page === 1 ? "#aaa" : "#004789",
                      cursor: page === 1 ? "default" : "pointer",
                      fontWeight: "500",
                    }}
                  >
                    ‹ Prev
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      style={{
                        padding: "6px 12px",
                        border: "1px solid #d0d0d0",
                        borderRadius: "6px",
                        background: p === page ? "#004789" : "white",
                        color: p === page ? "white" : "#004789",
                        cursor: "pointer",
                        fontWeight: p === page ? "600" : "400",
                      }}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    style={{
                      padding: "6px 14px",
                      border: "1px solid #d0d0d0",
                      borderRadius: "6px",
                      background: page === totalPages ? "#f5f5f5" : "white",
                      color: page === totalPages ? "#aaa" : "#004789",
                      cursor: page === totalPages ? "default" : "pointer",
                      fontWeight: "500",
                    }}
                  >
                    Next ›
                  </button>
                </div>
              </div>
            </>
          );
        })()}
      </div>
    </>
  );
};

export default UnacceptedFilingsSection;
